import React from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeOrderStatus } from '../utils/orderStatus';
import '../css/status-badge.css';

/**
 * Unified Status Badge Component for consistent status display across all pages
 * @param {Object} props
 * @param {string} props.type - Type of entity: 'order', 'user', 'complaint'
 * @param {string} props.status - Status value
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.compact - Compact size variant
 */
const StatusBadge = ({ type, status, className = '', compact = false }) => {
  const { t } = useTranslation();

  if (!status) return null;

  // Get status configuration based on type
  const getStatusConfig = () => {
    switch (type) {
      case 'order':
        return getOrderStatusConfig(status, t);
      case 'user':
        return getUserStatusConfig(status, t);
      case 'complaint':
        return getComplaintStatusConfig(status, t);
      default:
        return {
          label: status,
          className: 'status-badge-unknown',
          colorClass: 'status-unknown'
        };
    }
  };

  const config = getStatusConfig();
  const classes = [
    'status-badge',
    config.colorClass,
    compact && 'status-badge-compact',
    className
  ].filter(Boolean).join(' ');

  return (
    <span className={classes} title={config.label}>
      {config.label}
    </span>
  );
};

// Order status configuration
const getOrderStatusConfig = (rawStatus, t) => {
  const statusLabels = t('orders.statusLabels', { returnObjects: true });
  const status = normalizeOrderStatus(rawStatus);

  const statusMap = {
    'IN_PROGRESS': {
      label: statusLabels?.IN_PROGRESS || 'IN_PROGRESS',
      colorClass: 'status-order-in-progress'
    },
    'IN_WORK': {
      label: statusLabels?.IN_WORK || 'IN_WORK',
      colorClass: 'status-order-in-work'
    },
    'DONE': {
      label: statusLabels?.DONE || 'DONE',
      colorClass: 'status-order-done'
    },
    'CANCELED': {
      label: statusLabels?.CANCELED || 'CANCELED',
      colorClass: 'status-order-canceled'
    },
    'BLOCKED': {
      label: statusLabels?.BLOCKED || 'BLOCKED',
      colorClass: 'status-order-blocked'
    }
  };

  const config = statusMap[status] || {
    label: statusLabels?.UNKNOWN || status || 'Unknown',
    colorClass: 'status-unknown'
  };

  return config;
};

// User status configuration
const getUserStatusConfig = (status, t) => {
  const statusLabels = t('users.statuses', { returnObjects: true });
  
  const statusMap = {
    'ACTIVE': {
      label: statusLabels?.ACTIVE || 'ACTIVE',
      colorClass: 'status-user-active'
    },
    'INACTIVE': {
      label: statusLabels?.INACTIVE || 'INACTIVE',
      colorClass: 'status-user-inactive'
    },
    'BLOCKED': {
      label: statusLabels?.BLOCKED || 'BLOCKED',
      colorClass: 'status-user-blocked'
    }
  };

  const config = statusMap[status] || {
    label: statusLabels?.[status] || status || 'Unknown',
    colorClass: 'status-unknown'
  };

  return config;
};

// Complaint status configuration
const getComplaintStatusConfig = (status, t) => {
  const statusLabels = t('complaints.statuses', { returnObjects: true });
  
  const statusMap = {
    'PENDING': {
      label: statusLabels?.PENDING || 'PENDING',
      colorClass: 'status-complaint-pending'
    },
    'IN_REVIEW': {
      label: statusLabels?.IN_REVIEW || 'IN_REVIEW',
      colorClass: 'status-complaint-in-review'
    },
    'RESOLVED': {
      label: statusLabels?.RESOLVED || 'RESOLVED',
      colorClass: 'status-complaint-resolved'
    },
    'REJECTED': {
      label: statusLabels?.REJECTED || 'REJECTED',
      colorClass: 'status-complaint-rejected'
    }
  };

  const config = statusMap[status] || {
    label: statusLabels?.[status] || status || 'Unknown',
    colorClass: 'status-unknown'
  };

  return config;
};

export default StatusBadge;
